import React from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';

const DocumentShareForm = ({ 
  selectedBranches = [],
  onBranchesChange,
  selectedDepartments = [],
  onDepartmentsChange,
  selectedSubDepartments = [],
  onSubDepartmentsChange,
  branches = [],
  departments = [],
  subDepartments = [],
  filteredDepartments = [],
  filteredSubDepartments = [],
  availableUsers = [],
  selectedUsers = [],
  onUsersChange
}) => {
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#667eea' : '#e9ecef',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(102, 126, 234, 0.25)' : 'none',
      minHeight: '38px',
      '&:hover': {
        borderColor: '#ced4da'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:active': {
        backgroundColor: '#667eea'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d'
    })
  };

  // Handle branch change - filter departments
  const handleBranchChange = (selectedOptions) => {
    const branchIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    if (onBranchesChange) {
      onBranchesChange(branchIds, selectedOptions || []);
    }
  };

  // Handle department change - filter sub-departments
  const handleDepartmentChange = (selectedOptions) => {
    const departmentIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    if (onDepartmentsChange) {
      onDepartmentsChange(departmentIds, selectedOptions || []);
    }
  };

  // Handle sub-department change
  const handleSubDepartmentChange = (selectedOptions) => {
    const subDepartmentIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    if (onSubDepartmentsChange) {
      onSubDepartmentsChange(subDepartmentIds, selectedOptions || []);
    }
  };

  // Handle user change
  const handleUserChange = (selectedOptions) => {
    const userIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    if (onUsersChange) {
      onUsersChange(userIds, selectedOptions || []);
    }
  };

  return (
    <Form>
      {/* Branch Selection */}
      <Form.Group className="mb-3">
        <Form.Label className="text-label">
          Branch
        </Form.Label>
        <Select
          isMulti
          options={branches.map((branch) => ({
            value: branch.id,
            label: branch.name,
            data: branch,
          }))}
          value={selectedBranches.map(id => {
            const branch = branches.find(b => b.id === id);
            return branch ? { value: branch.id, label: branch.name, data: branch } : null;
          }).filter(Boolean)}
          onChange={handleBranchChange}
          isClearable
          placeholder="Select Branches"
          styles={customStyles}
        />
      </Form.Group>

      {/* Department Selection */}
      <Form.Group className="mb-3">
        <Form.Label className="text-label">
          Department
        </Form.Label>
        <Select
          isMulti
          options={(filteredDepartments.length > 0 ? filteredDepartments : departments).map((dept) => ({
            value: dept.id,
            label: dept.branch_name ? `${dept.name} (${dept.branch_name})` : dept.name,
            data: dept,
          }))}
          value={selectedDepartments.map(id => {
            const dept = (filteredDepartments.length > 0 ? filteredDepartments : departments).find(d => d.id === id);
            return dept ? { 
              value: dept.id, 
              label: dept.branch_name ? `${dept.name} (${dept.branch_name})` : dept.name, 
              data: dept 
            } : null;
          }).filter(Boolean)}
          onChange={handleDepartmentChange}
          isClearable
          placeholder="Select Departments"
          styles={customStyles}
        />
      </Form.Group>

      {/* Sub-Department Selection */}
      <Form.Group className="mb-3">
        <Form.Label className="text-label">
          Sub-Department
        </Form.Label>
        <Select
          isMulti
          options={(filteredSubDepartments.length > 0 ? filteredSubDepartments : subDepartments).map((sd) => ({
            value: sd.id || sd.sub_department_id,
            label: sd.name,
            data: sd,
          }))}
          value={selectedSubDepartments.map(id => {
            const sd = (filteredSubDepartments.length > 0 ? filteredSubDepartments : subDepartments).find(s => (s.id || s.sub_department_id) === id);
            return sd ? { 
              value: sd.id || sd.sub_department_id, 
              label: sd.name, 
              data: sd 
            } : null;
          }).filter(Boolean)}
          onChange={handleSubDepartmentChange}
          isClearable
          placeholder="Select Sub-Departments"
          styles={customStyles}
        />
      </Form.Group>

      {/* User Selection */}
      <Form.Group className="mb-3">
        <Form.Label className="text-label">
          Users
        </Form.Label>
        <Select
          isMulti
          options={availableUsers.map((user) => ({
            value: user.id || user.CorporateUserId,
            label: `${user.full_name || user.FullName || user.name}${((user.department_name || user.departmentName || user.branch_name || user.branchName) ? ` (${user.department_name || user.departmentName || ''}${(user.department_name || user.departmentName) && (user.branch_name || user.branchName) ? ', ' : ''}${user.branch_name || user.branchName || ''})` : '')}`,
            data: user,
          }))}
          value={selectedUsers.map(id => {
            const user = availableUsers.find(u => (u.id || u.CorporateUserId) === id);
            return user ? { 
              value: user.id || user.CorporateUserId, 
              label: `${user.full_name || user.FullName || user.name}${((user.department_name || user.departmentName || user.branch_name || user.branchName) ? ` (${user.department_name || user.departmentName || ''}${(user.department_name || user.departmentName) && (user.branch_name || user.branchName) ? ', ' : ''}${user.branch_name || user.branchName || ''})` : '')}`, 
              data: user 
            } : null;
          }).filter(Boolean)}
          onChange={handleUserChange}
          isClearable
          isSearchable
          placeholder="Select Users"
          styles={customStyles}
        />
        <small className="form-text text-muted">
          Select specific users who can access this document
        </small>
      </Form.Group>
    </Form>
  );
};

export default DocumentShareForm;

